/**
 * Number, money and date formatting — the primitives every rule and screen
 * builds on. Ported from the prototype exactly, including one quirk worth
 * knowing about (see `money`).
 */

/** parseFloat with empty/NaN → 0. Money is stored as typed (spec § 3). */
export const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

/** Locale-grouped fixed decimals; null/NaN renders as an em-dash. */
export const fmt = (n, d = 2) =>
  n == null || Number.isNaN(n)
    ? '—'
    : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/**
 * Money.
 *
 * NOTE, deliberately preserved: the prototype's guard reads
 * `n == null || isNaN(n) || n === 0 && false`. That last clause is dead code —
 * `&& false` can never be true — so a ZERO renders as "$0.00", not "—". It
 * looks like it was meant to dash out zeroes and never did.
 *
 * Keep it. Every screen, PDF and CSV in the approved prototype shows "$0.00"
 * for a zero, and "fixing" this would silently change output everywhere. The
 * spec's rule is that the prototype wins.
 */
export const money = (n, c = 'USD') =>
  n == null || Number.isNaN(n) ? '—' : (c === 'USD' ? '$' : `${c} `) + fmt(n);

/** Today as YYYY-MM-DD, local time. */
export const today = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Start of today, local — rule T1's boundary. */
export const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * Whole days from today until `dateStr` (YYYY-MM-DD). Negative = past.
 * Compared at local midnight on both sides so "today" is 0, not a fraction.
 */
export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - startOfToday()) / 86400000);
};

/** A date for display, falling back to the raw string if unparseable. */
export const dateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
};

/** Escape for anywhere HTML is built as a string (the report sheets). */
export const esc = (s) =>
  String(s == null ? '' : s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );

/** Digits only — for tel: and wa.me links. */
export const digits = (s) => String(s || '').replace(/\D/g, '');

/**
 * Phase / hall / booth as one label, without saying the hall twice.
 *
 * Canton Fair stands are written "10.2 C21" — hall-and-section, then the
 * stand. So a buyer who fills in Hall AND types the booth the way the sign
 * reads has the hall in both fields, and naively joining them produced
 * "10.2 10.2 C21" on the report, the revisit list and the PDF. Dropping the
 * hall when the booth already opens with it is the common case here, not an
 * edge case.
 *
 * `sep` differs by surface (' ' in dense tables, ' · ' in prose), and
 * `labelled` is for the supplier sheet, which spells out "Hall 10.2".
 */
export function boothLabel(supplier, { sep = ' ', labelled = false, fallback = '' } = {}) {
  const s = supplier || {};
  const hall = String(s.hall == null ? '' : s.hall).trim();
  const booth = String(s.booth == null ? '' : s.booth).trim();

  // Only a whole leading token counts: hall "10.2" covers booth "10.2 C21",
  // but hall "1" must not swallow the "1" of booth "10.2 C21".
  const boothRepeatsHall =
    !!hall && !!booth && (booth === hall || booth.startsWith(`${hall} `) || booth.startsWith(`${hall}.`));

  const parts = [
    s.phase ? (labelled ? `Phase ${s.phase}` : `P${s.phase}`) : null,
    boothRepeatsHall || !hall ? null : labelled ? `Hall ${hall}` : hall,
    booth ? (labelled ? `Booth ${booth}` : booth) : null,
  ];
  return parts.filter(Boolean).join(sep) || fallback;
}
